import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { authFetch, authFetchForm, fetchMenu } from "../../api";
import { FiTrash2, FiPlus } from "react-icons/fi";

const inputClass = "w-full px-3 py-1.5 rounded-md bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const selectStyle = { colorScheme: "dark" };
const optionStyle = { backgroundColor: "#13202D", color: "#ffffff" };
const emptyItem = { title: "", subtitle: "", how_to_develop: "", order: 0, is_active: true };

export default function SubServicesSettings() {
  const [items, setItems] = useState([]);
  const [imageFiles, setImageFiles] = useState({});
  const [error, setError] = useState("");
  const [newItem, setNewItem] = useState(emptyItem);
  const [newImageFile, setNewImageFile] = useState(null);
  const [addFormKey, setAddFormKey] = useState(0);
  const [mainCategories, setMainCategories] = useState([]);
  const [subServicesByCategory, setSubServicesByCategory] = useState({});

  const load = () => {
    authFetch("/core/sub-services/")
      .then((res) => res.json())
      .then((data) => setItems(data.sort((a, b) => a.order - b.order)))
      .catch(() => setError("Failed to load sub-services."));
  };

  useEffect(load, []);
  useEffect(() => {
    fetchMenu()
      .then((menuItems) => {
        const servicesMenu = menuItems.find((m) => m.parent === null && m.title === "Services");
        const categories = servicesMenu
          ? menuItems.filter((m) => m.parent === servicesMenu.id).sort((a, b) => a.order - b.order)
          : [];
        setMainCategories(categories);

        const byCategory = {};
        categories.forEach((cat) => {
          byCategory[cat.title] = menuItems
            .filter((m) => m.parent === cat.id)
            .sort((a, b) => a.order - b.order);
        });
        setSubServicesByCategory(byCategory);
      })
      .catch(() => {});
  }, []);

  const subtitleOptionsFor = (title) => subServicesByCategory[title] || [];

  const updateField = (id, field, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  };

  const updateTitle = (id, title) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, title, subtitle: "" } : it)));
  };

  const buildFormData = (item) => {
    const fd = new FormData();
    fd.append("title", item.title);
    fd.append("subtitle", item.subtitle);
    fd.append("how_to_develop", item.how_to_develop);
    fd.append("order", item.order);
    fd.append("is_active", item.is_active);
    return fd;
  };

  const saveItem = async (item) => {
    setError("");
    const fd = buildFormData(item);
    if (imageFiles[item.id]) fd.append("image", imageFiles[item.id]);
    const res = await authFetchForm(`/core/sub-services/${item.id}/`, fd);
    if (!res.ok) {
      setError("Failed to save sub-service.");
      return;
    }
    setImageFiles((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
    load();
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this sub-service?")) return;
    await authFetch(`/core/sub-services/${id}/`, { method: "DELETE" });
    load();
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.title || !newItem.subtitle) return;
    setError("");
    const fd = buildFormData({ ...newItem, order: items.length + 1 });
    if (newImageFile) fd.append("image", newImageFile);
    const res = await authFetchForm("/core/sub-services/", fd, "POST");
    if (!res.ok) {
      setError("Failed to add sub-service.");
      return;
    }
    setNewItem(emptyItem);
    setNewImageFile(null);
    setAddFormKey((k) => k + 1);
    load();
  };

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">Sub Services</h1>
      <p className="text-sm text-gray-400 mb-6">
        Pick a service and one of its sub-services, then add a "How to develop?" write-up and an image for it.
      </p>

      {error && <p className="text-red-400 mb-4">{error}</p>}

      <form key={addFormKey} onSubmit={addItem} className="mb-6 bg-navy border border-navy-border rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4">Add New Sub Service</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Title (Service)</label>
            <select
              className={inputClass}
              style={selectStyle}
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value, subtitle: "" })}
            >
              <option value="" style={optionStyle}>Select a service...</option>
              {mainCategories.map((cat) => (
                <option key={cat.id} value={cat.title} style={optionStyle}>{cat.title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Subtitle (Sub Service)</label>
            <select
              className={inputClass}
              style={selectStyle}
              value={newItem.subtitle}
              onChange={(e) => setNewItem({ ...newItem, subtitle: e.target.value })}
              disabled={!newItem.title}
            >
              <option value="" style={optionStyle}>Select a sub-service...</option>
              {subtitleOptionsFor(newItem.title).map((sub) => (
                <option key={sub.id} value={sub.title} style={optionStyle}>{sub.title}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">How to develop?</label>
          <div className="bg-white text-black rounded-md">
            <ReactQuill
              theme="snow"
              value={newItem.how_to_develop}
              onChange={(value) => setNewItem({ ...newItem, how_to_develop: value })}
            />
          </div>
        </div>

        <div className="flex items-center gap-6 mt-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setNewImageFile(e.target.files[0])}
              className="text-xs text-gray-400"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300 mt-4">
            <input
              type="checkbox"
              checked={newItem.is_active}
              onChange={(e) => setNewItem({ ...newItem, is_active: e.target.checked })}
            />
            Active
          </label>
          <button className="ml-auto mt-4 px-4 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold flex items-center gap-2">
            <FiPlus /> Add Sub Service
          </button>
        </div>
      </form>

      <div className="space-y-6">
        {items.map((item) => {
          const previewSrc = imageFiles[item.id] ? URL.createObjectURL(imageFiles[item.id]) : item.image;
          return (
            <div key={item.id} className="bg-navy border border-navy-border rounded-xl p-5">
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Image</label>
                  {previewSrc && (
                    <img src={previewSrc} alt={item.subtitle} className="w-full h-24 object-cover rounded-md border border-navy-border mb-1" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFiles((prev) => ({ ...prev, [item.id]: e.target.files[0] }))}
                    className="text-xs text-gray-400 w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Title (Service)</label>
                  <select
                    className={inputClass}
                    style={selectStyle}
                    value={item.title}
                    onChange={(e) => updateTitle(item.id, e.target.value)}
                  >
                    <option value="" style={optionStyle}>Select a service...</option>
                    {mainCategories.map((cat) => (
                      <option key={cat.id} value={cat.title} style={optionStyle}>{cat.title}</option>
                    ))}
                  </select>
                  <label className="block text-xs text-gray-400 mb-1 mt-3">Subtitle (Sub Service)</label>
                  <select
                    className={inputClass}
                    style={selectStyle}
                    value={item.subtitle}
                    onChange={(e) => updateField(item.id, "subtitle", e.target.value)}
                    disabled={!item.title}
                  >
                    <option value="" style={optionStyle}>Select a sub-service...</option>
                    {subtitleOptionsFor(item.title).map((sub) => (
                      <option key={sub.id} value={sub.title} style={optionStyle}>{sub.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Order</label>
                  <input
                    type="number"
                    className={inputClass}
                    value={item.order}
                    onChange={(e) => updateField(item.id, "order", Number(e.target.value))}
                  />
                  <label className="flex items-center gap-2 text-sm text-gray-300 mt-3">
                    <input
                      type="checkbox"
                      checked={item.is_active}
                      onChange={(e) => updateField(item.id, "is_active", e.target.checked)}
                    />
                    Active
                  </label>
                </div>
                <div className="flex md:justify-end items-start">
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="p-2 rounded-md text-red-400 hover:bg-red-500/10 flex items-center gap-1 text-xs"
                  >
                    <FiTrash2 size={14} /> Delete
                  </button>
                </div>
              </div>

              <label className="block text-xs text-gray-400 mb-1">How to develop?</label>
              <div className="bg-white text-black rounded-md mb-4">
                <ReactQuill
                  theme="snow"
                  value={item.how_to_develop}
                  onChange={(value) => updateField(item.id, "how_to_develop", value)}
                />
              </div>

              <button
                onClick={() => saveItem(item)}
                className="mt-4 px-4 py-2 rounded-md bg-brand-gradient text-white text-xs font-semibold"
              >
                Save
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
